/**
 * L'éditeur d'un bon d'intervention : ce que l'écran tient en mémoire, et sa traduction vers l'API (lot I1.1).
 *
 * Tout est en TEXTE tant que l'on saisit (une quantité « 8,5 », une valeur « 66 665 ») : la lecture
 * à la française se fait à l'envoi, jamais à la frappe. Rien n'est jamais un nombre à virgule flottante
 * jusqu'au serveur : les valeurs voyagent en chaînes décimales (un relevé est un `Decimal(18,6)`).
 *
 * Deux niveaux de contrôle, comme le serveur :
 * - `problemesDeSaisie` empêche d'ENREGISTRER (nombre illisible, date mal écrite) ;
 * - `problemesDeSoumission` empêche de SOUMETTRE (bon incomplet) — un brouillon incomplet se sauvegarde.
 * Le serveur reste l'autorité : ces contrôles ne font qu'éviter un aller-retour voué au refus.
 *
 * Les règles dépendent du MODÈLE DE FEUILLE de la nature du bon (entretien, dépannage, installation, livraison, location) :
 * `ContexteBon.modele` ; sans lui, c'est l'entretien, comme avant l'arrivée des modèles.
 *
 * AUCUN prix ni total : un bon n'en porte pas (ADR-0011).
 */
import type {
  BonDetail,
  ChoixDossier,
  Compteur,
  ContenuBon,
  ControleBon,
  EntreeCreationBon,
  EntreeSauvegardeBon,
  FacturationBon,
  GenreLigneBon,
  GenreSuiteBon,
  LignePrevue,
  MaintenanceMachine,
  ModeleBon,
  NatureBon,
  ValeurStandardNature,
} from '../api/client';
import { genrePermis, MODELE_PAR_DEFAUT } from './bon-modeles';
import { heuresDepuisBon, heuresEnvoyees, problemesHeures, type HeureEditable } from './bon-heures';
import { nouvelleCle } from './gammes-edition';
import { DESIGNATION_PAR_DEFAUT, UNITE_PAR_DEFAUT, aujourdhuiLocal, interventionAFaire, jourFr } from './interventions';

/**
 * D'où vient une ligne versée par le bouton « Ajouter les pièces du palier » : la gamme et le code du palier (la pastille
 * « gamme B »). ENREGISTRÉ dans le brouillon : c'est ce qui permet, en choisissant un autre palier, de remplacer ce que le bouton
 * avait versé au lieu de l'ajouter par-dessus (les paliers sont cumulatifs : les pièces seraient en double).
 */
export interface OrigineLigne {
  readonly gammeId: string;
  readonly palier: string;
}

export interface LigneBonEditable {
  readonly lineKey: string;
  readonly kind: GenreLigneBon;
  readonly reference: string;
  readonly designation: string;
  /** Texte saisi : « 8 », « 1,5 ». */
  readonly quantite: string;
  readonly unite: string;
  readonly note: string;
  /** Le n° de série d'un matériel (livraison, installation) ; vide pour toute autre ligne. */
  readonly serie: string;
  /** Absent pour une ligne saisie à la main ; présent pour une ligne versée par le bouton des pièces du palier. */
  readonly versee?: OrigineLigne;
}

/** Les champs propres au modèle de feuille, en texte tant qu'on saisit. */
export interface DetailsEditable {
  /** Dépannage : ce que le client a signalé. */
  readonly problemeSignale: string;
  /** Dépannage : cause trouvée, ce qui a été fait. */
  readonly travauxRealises: string;
  /** Livraison : les réserves du client à la réception. */
  readonly reserves: string;
  /** Installation : dossier technique et certificats ; jamais présélectionné. */
  readonly dossier: ChoixDossier | '';
  /** Location : AAAA-MM-JJ de la mise à disposition, ou vide. */
  readonly locationDu: string;
  /** Location : AAAA-MM-JJ du retour prévu, ou vide. */
  readonly locationAu: string;
}

export interface SuiteEditable {
  readonly followUpKey: string;
  readonly kind: GenreSuiteBon;
  readonly comment: string;
}

/** Un contrôle de la nature, fait ou non : le libellé est l'instantané de la liste de la nature au moment où il est posé sur le bon. */
export interface ControleEditable {
  readonly checkKey: string;
  readonly label: string;
  readonly checked: boolean;
}

export interface BonEditable {
  /** Vide tant que la nature n'est pas choisie : une présélection se retape sans réfléchir. */
  readonly natureId: string;
  readonly praxedoNumber: string;
  readonly contactLabel: string;
  /** Sous contrat ou facturé ; vide tant qu'on ne l'a pas choisi (jamais présélectionné : le bon en dépend). */
  readonly facturation: FacturationBon | '';
  /** Les champs propres au modèle de feuille. */
  readonly details: DetailsEditable;
  /** Les techniciens de l'intervention, par leur nom : une intervention n'est pas toujours faite seul. */
  readonly techniciens: readonly string[];
  /** AAAA-MM-JJ, ou vide : la date n'est jamais présélectionnée (un bon se saisit le plus souvent après coup). */
  readonly performedOn: string;
  readonly observations: string;
  /** Texte saisi par compteur de la machine ; une case vide n'envoie aucun relevé. */
  readonly valeursReleves: Readonly<Record<string, string>>;
  readonly lignes: readonly LigneBonEditable[];
  /** Le palier de gamme réalisé ; jamais présélectionné. */
  readonly levelKey: string | null;
  readonly suites: readonly SuiteEditable[];
  /** Les contrôles de la nature, dans l'ordre d'impression, avec ce qui a été fait. Jamais cochés d'office. */
  readonly controles: readonly ControleEditable[];
  /** Les heures de l'équipe, par technicien et par jour (écrites dans l'équipe de l'intervention à l'enregistrement). */
  readonly heures: readonly HeureEditable[];
}

// ------------------------------------------------------------------- lecture des nombres

export interface OptionsDecimale {
  readonly negatifPermis: boolean;
  readonly chiffresEntiers: number;
  readonly decimales: number;
}

/** Un relevé de compteur : `Decimal(18,6)`, négatif permis (un compteur « T° » n'est pas monotone). */
export const OPTIONS_RELEVE: OptionsDecimale = { negatifPermis: true, chiffresEntiers: 12, decimales: 6 };
/** Une quantité : `Decimal(14,3)`, jamais négative. */
export const OPTIONS_QUANTITE: OptionsDecimale = { negatifPermis: false, chiffresEntiers: 11, decimales: 3 };

export type LectureDecimale =
  | { readonly ok: true; readonly vide: true; readonly valeur: '' }
  | { readonly ok: true; readonly vide: false; readonly valeur: string }
  | { readonly ok: false; readonly erreur: string };

/**
 * Lit un nombre saisi à la française : « 1 334,5 » → « 1334.5 », chaîne canonique (point, pas de zéro de tête ni de
 * décimale vide), comme le serveur. Vide = vide. Refuse tout ce qui n'est pas un nombre au lieu de le changer en 0 :
 * un « 5O » (lettre O) ne doit pas devenir « 0 ».
 */
export function lireDecimal(texte: string, options: OptionsDecimale): LectureDecimale {
  const sansEspaces = texte.replace(/[\s  ]/g, '');
  if (sansEspaces === '') return { ok: true, vide: true, valeur: '' };

  if (sansEspaces.includes('.') && sansEspaces.includes(',')) {
    return { ok: false, erreur: `« ${texte.trim()} » : écrivez 1334,5 (une seule virgule, pas de point entre les milliers).` };
  }
  const normalise = sansEspaces.replace(',', '.');
  const motif = options.negatifPermis ? /^-?\d+(\.\d+)?$/ : /^\d+(\.\d+)?$/;
  if (!motif.test(normalise)) {
    return {
      ok: false,
      erreur: `« ${texte.trim()} » n’est pas un nombre${options.negatifPermis ? '' : ' positif'}.`,
    };
  }

  const [entier = '', fraction = ''] = normalise.replace(/^-/, '').split('.');
  const entierCanonique = entier.replace(/^0+(?=\d)/, '');
  if (entierCanonique.length > options.chiffresEntiers) {
    return { ok: false, erreur: `« ${texte.trim()} » est trop grand.` };
  }
  if (fraction.length > options.decimales) {
    return { ok: false, erreur: `« ${texte.trim()} » a plus de ${options.decimales} décimales.` };
  }

  const fractionCanonique = fraction.replace(/0+$/, '');
  const absolu = fractionCanonique === '' ? entierCanonique : `${entierCanonique}.${fractionCanonique}`;
  const negatif = normalise.startsWith('-') && absolu !== '0';
  return { ok: true, vide: false, valeur: negatif ? `-${absolu}` : absolu };
}

/** Le texte à montrer dans un champ : la virgule française, pas de séparateur de milliers. */
export function texteDepuisDecimal(valeur: string | null | undefined): string {
  return valeur ? valeur.replace('.', ',') : '';
}

// ------------------------------------------------------------------- constructeurs

export const DETAILS_EDITABLES_VIDES: DetailsEditable = {
  problemeSignale: '',
  travauxRealises: '',
  reserves: '',
  dossier: '',
  locationDu: '',
  locationAu: '',
};

export function bonVide(natureId = ''): BonEditable {
  return {
    natureId,
    praxedoNumber: '',
    contactLabel: '',
    facturation: '',
    details: DETAILS_EDITABLES_VIDES,
    techniciens: [],
    performedOn: '',
    observations: '',
    valeursReleves: {},
    lignes: [],
    levelKey: null,
    suites: [],
    controles: [],
    heures: [],
  };
}

export function ligneBonVide(kind: GenreLigneBon = 'PART'): LigneBonEditable {
  return {
    lineKey: nouvelleCle(),
    kind,
    reference: '',
    designation: DESIGNATION_PAR_DEFAUT[kind],
    quantite: '',
    unite: UNITE_PAR_DEFAUT[kind],
    note: '',
    serie: '',
  };
}

export function suiteVide(kind: GenreSuiteBon = 'QUOTE_TO_DO'): SuiteEditable {
  return { followUpKey: nouvelleCle(), kind, comment: '' };
}

/** L'état de l'éditeur depuis un bon relu du serveur. */
export function versEditable(bon: BonDetail): BonEditable {
  const contenu: ContenuBon = bon.content ?? {
    readings: [],
    lines: [],
    levelKey: null,
    followUps: [],
    technicians: [],
    checks: [],
    details: bon.details,
  };
  return {
    natureId: bon.nature.id,
    praxedoNumber: bon.praxedoNumber ?? '',
    contactLabel: bon.contactLabel ?? '',
    facturation: bon.billing ?? '',
    details: {
      problemeSignale: contenu.details.reportedProblem,
      travauxRealises: contenu.details.workDone,
      reserves: contenu.details.reservations,
      dossier: contenu.details.dossier ?? '',
      locationDu: contenu.details.rentalFrom ?? '',
      locationAu: contenu.details.rentalReturn ?? '',
    },
    techniciens: contenu.technicians,
    performedOn: bon.performedOn ?? '',
    observations: bon.observations ?? '',
    valeursReleves: Object.fromEntries(contenu.readings.map((r) => [r.counterId, texteDepuisDecimal(r.value)])),
    lignes: contenu.lines.map((l) => ({
      lineKey: l.lineKey,
      kind: l.kind,
      reference: l.reference ?? '',
      designation: l.designation,
      quantite: texteDepuisDecimal(l.quantity),
      unite: l.unit ?? '',
      note: l.note ?? '',
      serie: l.serialNumber ?? '',
      ...(l.versedFrom ? { versee: { gammeId: l.versedFrom.profileId, palier: l.versedFrom.levelCode } } : {}),
    })),
    levelKey: contenu.levelKey,
    suites: contenu.followUps.map((s) => ({ followUpKey: s.followUpKey, kind: s.kind, comment: s.comment })),
    controles: contenu.checks.map((c) => ({ checkKey: c.checkKey, label: c.label, checked: c.checked })),
    // Un bon lu d'avant les heures dans le bon (ou un bon validé) peut n'en porter aucune.
    heures: heuresDepuisBon(bon.planification?.heures ?? []),
  };
}

/**
 * Le jour que le planning propose pour la date du bon : le DERNIER jour travaillé qui n'est pas dans le futur ; quand tous les
 * jours prévus sont à venir, le PREMIER (on prépare le bon à l'avance : il s'enregistre avec ce jour, mais ne se soumet qu'à
 * partir de lui). Rien quand l'intervention n'a aucun jour.
 */
export function jourProposeDuPlanning(jours: readonly string[], aujourdhui: string): string {
  const passes = jours.filter((j) => j <= aujourdhui);
  if (passes.length > 0) return passes[passes.length - 1]!;
  return jours[0] ?? '';
}

/**
 * « Saisir le bon » depuis le planning : une intervention encore à faire reprend ce que le planning a prévu — le jour (le
 * dernier jour travaillé) et l'équipe. C'est une PROPOSITION : elle n'écrase jamais ce qui est déjà saisi, et comme l'éditeur
 * la compare à l'état enregistré, elle compte pour une modification à enregistrer. Un autre état de bon : rien à proposer.
 */
export function proposerDepuisPlanning(editable: BonEditable, bon: BonDetail, aujourdhui: string): BonEditable {
  if (!interventionAFaire(bon.status)) return editable;
  return {
    ...editable,
    performedOn: editable.performedOn !== '' ? editable.performedOn : jourProposeDuPlanning(bon.planification.jours, aujourdhui),
    techniciens: editable.techniciens.length > 0 ? editable.techniciens : bon.planification.equipe.slice(0, PLAFOND_TECHNICIENS),
  };
}

/**
 * « Saisir le bon » sur une intervention dont le bon n'a encore AUCUN contenu enregistré : les pièces que le planning prévoit (palier
 * choisi, lues dans la gamme, jamais enregistrées) deviennent les articles de départ du bon — sans elles, le bon s'ouvrirait vide
 * alors que le planning les montre. C'est une PROPOSITION (elle n'écrase jamais une ligne déjà saisie, et compte pour une
 * modification à enregistrer) ; chaque ligne reste modifiable et retirable. Rien à proposer pour un bon qui a déjà un contenu : ses
 * lignes enregistrées font foi, même vides.
 */
export function proposerLignesDuPlanning(
  editable: BonEditable,
  bon: Pick<BonDetail, 'status' | 'content'>,
  prevues: readonly LignePrevue[],
  cle: () => string,
): BonEditable {
  if (!interventionAFaire(bon.status) || bon.content !== null || editable.lignes.length > 0 || prevues.length === 0) return editable;
  return {
    ...editable,
    lignes: prevues.map((l) => ({
      lineKey: cle(),
      kind: l.kind,
      reference: l.reference ?? '',
      designation: l.designation,
      quantite: texteDepuisDecimal(l.quantite),
      unite: l.unite ?? '',
      note: l.note ?? '',
      serie: l.serialNumber ?? '',
      ...(l.versedFrom ? { versee: { gammeId: l.versedFrom.profileId, palier: l.versedFrom.levelCode } } : {}),
    })),
  };
}

export function estModifie(origine: BonEditable, courant: BonEditable): boolean {
  return JSON.stringify(origine) !== JSON.stringify(courant);
}

// ------------------------------------------------------------------- opérations de liste

/** Au plus ce nombre de techniciens sur un bon (le serveur refuse au-delà). */
export const PLAFOND_TECHNICIENS = 20;

/**
 * La clé d'un nom de technicien : sans casse, accents, espaces ni ponctuation. « Gregory BONFILS » et « gregory  bonfils »
 * sont le même technicien. C'est la règle du serveur (`cleNomTechnicien`) : une seule façon de comparer des deux côtés.
 */
export function cleNomTechnicien(nom: string): string {
  // Décomposé (« é » devient « e » puis son accent), l'accent n'est ni une lettre ni un chiffre : le filtre final le retire
  // avec les espaces et la ponctuation.
  return nom
    .normalize('NFD')
    .toUpperCase()
    .replace(/[^\p{L}\p{N}]/gu, '');
}

/**
 * Ajoute un technicien : nom rogné sur une ligne ; ignoré s'il est vide, déjà présent (même clé) ou si la liste est pleine.
 * Un nom que le serveur connaît déjà reprend SA graphie : « stephane tulli » devient « Stephane TULLI », pour que les bons
 * portent tous le même écrit d'une même personne.
 */
export function ajouterTechnicien(
  bon: BonEditable,
  nom: string,
  connus: readonly { readonly name: string }[] = [],
): BonEditable {
  const saisi = nom.trim().replace(/\s+/g, ' ');
  const cle = cleNomTechnicien(saisi);
  if (cle === '' || bon.techniciens.length >= PLAFOND_TECHNICIENS) return bon;
  if (bon.techniciens.some((t) => cleNomTechnicien(t) === cle)) return bon;
  const propre = connus.find((c) => cleNomTechnicien(c.name) === cle)?.name ?? saisi;
  return { ...bon, techniciens: [...bon.techniciens, propre] };
}

export function retirerTechnicien(bon: BonEditable, nom: string): BonEditable {
  const cle = cleNomTechnicien(nom);
  return { ...bon, techniciens: bon.techniciens.filter((t) => cleNomTechnicien(t) !== cle) };
}

/** Les noms du menu : ceux que le serveur connaît, moins ceux déjà sur le bon (dans l'ordre du serveur). */
export function techniciensAProposer(connus: readonly { readonly name: string }[], bon: BonEditable): string[] {
  const choisis = new Set(bon.techniciens.map(cleNomTechnicien));
  return connus.map((c) => c.name).filter((nom) => !choisis.has(cleNomTechnicien(nom)));
}

/**
 * Une valeur décimale (chaîne canonique) pour la LECTURE : virgule française et séparateur de milliers (« 71 423,5 »).
 * Le séparateur est l'espace insécable ORDINAIRE (U+00A0), et non l'espace fine insécable (U+202F) de `Intl` : à 12-13 px,
 * celle-ci ne mesure que ~1,8 px et ne se voit pas (« 71423 »). Elle ne se coupe pas en fin de ligne, et `lireDecimal`
 * l'accepte au collage (`\s`).
 * Jamais pour un champ de saisie (là, `texteDepuisDecimal` : pas de séparateur, on retape sans l'avoir à effacer).
 */
export function valeurAffichee(valeur: string | null | undefined): string {
  if (!valeur) return '';
  const negatif = valeur.startsWith('-');
  const [entier = '', fraction] = valeur.replace(/^-/, '').split('.');
  const groupe = entier.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
  return `${negatif ? '-' : ''}${groupe}${fraction ? `,${fraction}` : ''}`;
}

export function majReleve(bon: BonEditable, counterId: string, texte: string): BonEditable {
  return { ...bon, valeursReleves: { ...bon.valeursReleves, [counterId]: texte } };
}

/**
 * Une ligne « EN PLUS » (2026-10-24) : une pièce, une main d'œuvre ou un déplacement ajoutés à la main, sans origine de gamme.
 * Toujours facturée, même sous contrat annuel (le chiffrage le dit) ; l'éditeur la marque « en plus ». Une ligne versée depuis la
 * gamme fait partie de la visite.
 */
export function estEnPlus(kind: GenreLigneBon): boolean {
  return kind === 'PART' || kind === 'LABOR' || kind === 'TRAVEL';
}

/** Le palier d'où viennent les pièces versées du bon (« B ») ; `null` s'il n'y en a pas ou s'il y en a de plusieurs paliers. */
export function palierVerse(lignes: readonly LigneBonEditable[]): string | null {
  const paliers = new Set(lignes.flatMap((l) => (l.versee ? [l.versee.palier] : [])));
  return paliers.size === 1 ? [...paliers][0]! : null;
}

export function ajouterLigneBon(bon: BonEditable, kind: GenreLigneBon = 'PART'): BonEditable {
  return { ...bon, lignes: [...bon.lignes, ligneBonVide(kind)] };
}

export function retirerLigneBon(bon: BonEditable, index: number): BonEditable {
  return { ...bon, lignes: bon.lignes.filter((_, i) => i !== index) };
}

export function majLigneBon(bon: BonEditable, index: number, changement: Partial<Omit<LigneBonEditable, 'lineKey'>>): BonEditable {
  return { ...bon, lignes: bon.lignes.map((l, i) => (i === index ? { ...l, ...changement } : l)) };
}

/**
 * Changer le type d'une ligne propose son unité et sa désignation usuelles SI elles n'ont pas été saisies à la main
 * (une désignation vide compte comme non saisie).
 */
export function changerTypeLigne(bon: BonEditable, index: number, kind: GenreLigneBon): BonEditable {
  const ligne = bon.lignes[index];
  if (!ligne) return bon;
  const uniteInchangee = ligne.unite === UNITE_PAR_DEFAUT[ligne.kind];
  const designationInchangee = ligne.designation.trim() === '' || ligne.designation === DESIGNATION_PAR_DEFAUT[ligne.kind];
  return majLigneBon(bon, index, {
    kind,
    ...(uniteInchangee ? { unite: UNITE_PAR_DEFAUT[kind] } : {}),
    ...(designationInchangee ? { designation: DESIGNATION_PAR_DEFAUT[kind] } : {}),
  });
}

export function deplacerLigneBon(bon: BonEditable, index: number, sens: -1 | 1): BonEditable {
  const cible = index + sens;
  if (cible < 0 || cible >= bon.lignes.length) return bon;
  const lignes = [...bon.lignes];
  const a = lignes[index]!;
  const b = lignes[cible]!;
  lignes[index] = b;
  lignes[cible] = a;
  return { ...bon, lignes };
}

export function ajouterSuite(bon: BonEditable, kind: GenreSuiteBon = 'QUOTE_TO_DO'): BonEditable {
  return { ...bon, suites: [...bon.suites, suiteVide(kind)] };
}

export function retirerSuite(bon: BonEditable, index: number): BonEditable {
  return { ...bon, suites: bon.suites.filter((_, i) => i !== index) };
}

// ------------------------------------------------------------------- contrôles de la nature

/**
 * Les contrôles d'un bon quand on choisit (ou change) sa nature : ceux de la liste de la nature, dans son ordre, avec
 * leur libellé du moment. Ce qui était déjà coché reste coché pour la MÊME clé (re-choisir la même nature ne fait rien
 * perdre) ; une autre nature a d'autres clés, donc repart de zéro. Rien n'est jamais coché d'office : un contrôle fait
 * se coche à la main, sinon le papier mentirait.
 */
export function controlesDeLaNature(
  nature: Pick<NatureBon, 'checkItems'> | null | undefined,
  anciens: readonly ControleEditable[] = [],
): ControleEditable[] {
  if (!nature) return [];
  const faits = new Set(anciens.filter((c) => c.checked).map((c) => c.checkKey));
  return nature.checkItems.map((e) => ({ checkKey: e.key, label: e.label, checked: faits.has(e.key) }));
}

/** Coche ou décoche UN contrôle. */
export function basculerControle(bon: BonEditable, checkKey: string): BonEditable {
  return { ...bon, controles: bon.controles.map((c) => (c.checkKey === checkKey ? { ...c, checked: !c.checked } : c)) };
}

/** Coche (ou décoche) tous les contrôles d'un geste : le cas courant d'un entretien complet. */
export function cocherTousLesControles(bon: BonEditable, coche: boolean): BonEditable {
  return { ...bon, controles: bon.controles.map((c) => ({ ...c, checked: coche })) };
}

/** Le nombre de contrôles faits, pour la phrase « 9 sur 13 ». */
export function nombreControlesFaits(controles: readonly ControleEditable[]): number {
  return controles.filter((c) => c.checked).length;
}

export function majSuite(bon: BonEditable, index: number, changement: Partial<Omit<SuiteEditable, 'followUpKey'>>): BonEditable {
  return { ...bon, suites: bon.suites.map((s, i) => (i === index ? { ...s, ...changement } : s)) };
}

// ------------------------------------------------------------------- compteurs et paliers

/**
 * Un relevé « en heures » : l'unité le dit (« h », « heures »), ou — sans unité — le libellé (« Heures totales »). Les heures gardent
 * leur règle (heures totales, heures en charge) : elles ne sont jamais des valeurs standard de la feuille.
 */
export function estReleveEnHeures(unite: string | null | undefined, libelle: string | null | undefined): boolean {
  const u = (unite ?? '').trim().toLowerCase();
  if (u === 'h' || u === 'heure' || u === 'heures' || u === 'hr') return true;
  return u === '' && /heures?\b/i.test(libelle ?? '');
}

/** La clé qui rapproche le libellé d'un compteur de celui d'une valeur standard : sans casse, accents, espaces ni ponctuation. */
export function cleLibelleValeur(libelle: string): string {
  return cleNomTechnicien(libelle);
}

/**
 * Le début de l'identifiant d'une ligne de saisie qui n'est PAS ENCORE un compteur de la machine : une valeur standard de la
 * feuille (T° ambiante…) que la machine n'a pas. Le compteur n'est créé qu'à l'enregistrement du bon, et seulement si une valeur
 * y est saisie : la machine ne se remplit pas de compteurs vides.
 */
const PREFIXE_A_CREER = 'standard:';

export function estCompteurACreer(compteur: Pick<Compteur, 'id'>): boolean {
  return compteur.id.startsWith(PREFIXE_A_CREER);
}

/** La clé de la valeur standard d'une ligne à créer (`TEMP_AMBIENT`) ; `null` pour un vrai compteur. */
export function cleStandardDe(compteur: Pick<Compteur, 'id'>): string | null {
  return estCompteurACreer(compteur) ? compteur.id.slice(PREFIXE_A_CREER.length) : null;
}

/**
 * Les lignes de la rubrique « Valeurs relevées », dans l'ordre de saisie : le compteur des entretiens (les heures totales),
 * les autres heures, puis les valeurs standard de la feuille dans l'ordre de la feuille — le compteur de la machine quand il
 * existe (même libellé, sans tenir compte de la casse ni des accents), sinon une ligne À CRÉER —, enfin les autres compteurs de
 * la machine par libellé.
 */
export function compteursPourSaisie(
  compteurs: readonly Compteur[],
  standard: readonly ValeurStandardNature[] = [],
  assetId = '',
): Compteur[] {
  const rangs = new Map(standard.map((v, i) => [cleLibelleValeur(v.label), i]));
  const pris = new Set<string>();
  const lignes: { readonly compteur: Compteur; readonly groupe: number; readonly rang: number }[] = [];

  for (const compteur of compteurs) {
    const cle = cleLibelleValeur(compteur.label);
    const rang = rangs.get(cle);
    if (compteur.drivesMaintenance) lignes.push({ compteur, groupe: 0, rang: 0 });
    else if (estReleveEnHeures(compteur.unit, compteur.label)) lignes.push({ compteur, groupe: 1, rang: 0 });
    else if (rang !== undefined) {
      pris.add(cle);
      lignes.push({ compteur, groupe: 2, rang });
    } else lignes.push({ compteur, groupe: 3, rang: 0 });
  }

  const machine = assetId !== '' ? assetId : (compteurs[0]?.assetId ?? '');
  standard.forEach((valeur, rang) => {
    if (pris.has(cleLibelleValeur(valeur.label))) return;
    lignes.push({
      groupe: 2,
      rang,
      compteur: {
        id: `${PREFIXE_A_CREER}${valeur.key}`,
        assetId: machine,
        label: valeur.label,
        unit: valeur.unit,
        monotonic: false,
        drivesMaintenance: false,
        dernier: null,
        nombreReleves: 0,
      },
    });
  });

  return lignes
    .sort((a, b) => {
      if (a.groupe !== b.groupe) return a.groupe - b.groupe;
      if (a.groupe === 2 && a.rang !== b.rang) return a.rang - b.rang;
      return a.compteur.label.localeCompare(b.compteur.label, 'fr');
    })
    .map((l) => l.compteur);
}

/**
 * Les valeurs standard dont une valeur LISIBLE est saisie sur une ligne qui n'est pas encore un compteur : leurs clés, à faire créer
 * sur la machine avant d'enregistrer le bon. Une ligne vide ne crée rien.
 */
export function valeursACreer(bon: BonEditable, lignes: readonly Compteur[]): string[] {
  const cles: string[] = [];
  for (const ligne of lignes) {
    const cle = cleStandardDe(ligne);
    if (cle === null) continue;
    const lecture = lireDecimal(bon.valeursReleves[ligne.id] ?? '', OPTIONS_RELEVE);
    if (lecture.ok && !lecture.vide) cles.push(cle);
  }
  return cles;
}

/**
 * Une fois les compteurs créés (ou retrouvés) sur la machine : chaque ligne à créer prend l'identifiant du compteur réel, et la valeur
 * saisie la suit. Une ligne à créer sans valeur reste telle quelle (elle n'envoie rien).
 */
export function avecComptesCrees(
  bon: BonEditable,
  lignes: readonly Compteur[],
  crees: readonly { readonly key: string; readonly counterId: string }[],
): { readonly bon: BonEditable; readonly compteurs: Compteur[] } {
  const parCle = new Map(crees.map((c) => [c.key, c.counterId]));
  const valeurs: Record<string, string> = { ...bon.valeursReleves };
  const compteurs = lignes.map((ligne) => {
    const cle = cleStandardDe(ligne);
    const reel = cle === null ? undefined : parCle.get(cle);
    if (reel === undefined) return ligne;
    if (ligne.id in valeurs) {
      valeurs[reel] = valeurs[ligne.id] ?? '';
      delete valeurs[ligne.id];
    }
    return { ...ligne, id: reel };
  });
  return { bon: { ...bon, valeursReleves: valeurs }, compteurs };
}

export interface PalierChoix {
  readonly levelKey: string;
  readonly code: string;
  readonly label: string;
  readonly rank: number;
}

/** Les paliers de la version EN VIGUEUR de la gamme rattachée, du moins englobant au plus ; vide sans gamme. */
export function paliersDeLaGamme(maintenance: MaintenanceMachine | null): PalierChoix[] {
  const niveaux = maintenance?.rattachement?.version?.contenu.levels ?? [];
  return niveaux
    .map((n) => ({ levelKey: n.levelKey, code: n.code, label: n.label, rank: n.rank }))
    .sort((a, b) => a.rank - b.rank);
}

// ------------------------------------------------------------------- contrôles

export interface ContexteBon {
  /** Le modèle de feuille de la nature choisie ; l'entretien quand il manque (nature non choisie, ancien appel). */
  readonly modele?: ModeleBon;
  /** Les compteurs de la machine du bon (aucun pour une livraison sur un site seul). */
  readonly compteurs: readonly Compteur[];
  /** La nature choisie enregistre-t-elle un entretien réalisé ? */
  readonly natureEnregistreEntretien: boolean;
  /** Les paliers de la gamme rattachée (vide : aucune gamme, ou aucune version en vigueur). */
  readonly paliers: readonly PalierChoix[];
  /** AAAA-MM-JJ : sert à refuser une date dans le futur. */
  readonly aujourdhui?: string;
  /**
   * Une intervention PLANIFIÉE se prépare à l'avance : son bon peut porter le jour prévu, même à venir. Il s'enregistre, mais ne
   * se soumet qu'à partir de ce jour (l'intervention ne peut pas avoir eu lieu dans le futur).
   */
  readonly dateFuturePermise?: boolean;
}

const JOUR = /^\d{4}-\d{2}-\d{2}$/;

/** Le jour AAAA-MM-JJ existe-t-il ? (un 30 février n'existe pas.) */
export function jourExiste(jour: string): boolean {
  if (!JOUR.test(jour)) return false;
  const ms = Date.parse(`${jour}T00:00:00.000Z`);
  return !Number.isNaN(ms) && new Date(ms).toISOString().slice(0, 10) === jour;
}

/** Ce qui empêche d'ENREGISTRER : un nombre illisible ou une date impossible. Un brouillon incomplet, lui, s'enregistre. */
export function problemesDeSaisie(bon: BonEditable, contexte: Pick<ContexteBon, 'compteurs' | 'aujourdhui' | 'dateFuturePermise'>): string[] {
  const problemes: string[] = [];
  const aujourdhui = contexte.aujourdhui ?? aujourdhuiLocal();

  if (bon.performedOn !== '') {
    if (!jourExiste(bon.performedOn)) problemes.push('La date de l’intervention n’existe pas.');
    else if (bon.performedOn > aujourdhui && contexte.dateFuturePermise !== true) problemes.push('La date de l’intervention ne peut pas être dans le futur.');
  }

  for (const compteur of contexte.compteurs) {
    const texte = bon.valeursReleves[compteur.id] ?? '';
    const lecture = lireDecimal(texte, OPTIONS_RELEVE);
    if (!lecture.ok) problemes.push(`Compteur « ${compteur.label} » : ${lecture.erreur}`);
  }

  bon.lignes.forEach((l, i) => {
    const lecture = lireDecimal(l.quantite, OPTIONS_QUANTITE);
    if (!lecture.ok) problemes.push(`Ligne ${i + 1} : ${lecture.erreur}`);
  });

  // La location : les deux jours, s'ils sont saisis, existent et le retour ne précède pas la mise à disposition.
  const { locationDu, locationAu } = bon.details;
  if (locationDu !== '' && !jourExiste(locationDu)) problemes.push('La date de mise à disposition n’existe pas.');
  if (locationAu !== '' && !jourExiste(locationAu)) problemes.push('La date de retour prévu n’existe pas.');
  if (locationDu !== '' && locationAu !== '' && jourExiste(locationDu) && jourExiste(locationAu) && locationAu < locationDu) {
    problemes.push('Le retour prévu ne peut pas précéder la mise à disposition.');
  }

  problemes.push(...problemesHeures(bon.heures, aujourdhui));

  return problemes;
}

/** Les relevés renseignés, en chaînes décimales canoniques ; une valeur illisible est ignorée (les problèmes de saisie la signalent). */
function relevesEnvoyes(bon: BonEditable, compteurs: readonly Compteur[]): ContenuBon['readings'] {
  const lus: { counterId: string; value: string }[] = [];
  for (const compteur of compteurs) {
    const lecture = lireDecimal(bon.valeursReleves[compteur.id] ?? '', OPTIONS_RELEVE);
    if (lecture.ok && !lecture.vide) lus.push({ counterId: compteur.id, value: lecture.valeur });
  }
  return lus;
}

/**
 * Ce que le bon doit porter pour passer « à valider » : le miroir de `problemesPourSoumission` du serveur, plus les
 * conditions du palier réalisé. Liste vide = soumettable (le serveur re-vérifie tout).
 */
export function problemesDeSoumission(bon: BonEditable, contexte: ContexteBon): string[] {
  const problemes = [...problemesDeSaisie(bon, contexte)];
  const modele = contexte.modele ?? MODELE_PAR_DEFAUT;

  if (bon.performedOn === '') problemes.push('La date de l’intervention n’est pas renseignée.');
  else if (contexte.dateFuturePermise === true && jourExiste(bon.performedOn) && bon.performedOn > (contexte.aujourdhui ?? aujourdhuiLocal())) {
    problemes.push(`L’intervention n’a pas encore eu lieu (prévue le ${jourFr(bon.performedOn)}) : le bon ne se soumet qu’à partir de son jour.`);
  }
  if (bon.techniciens.length === 0) problemes.push('Aucun technicien n’est renseigné : ajoutez-en au moins un.');
  if (bon.facturation === '') {
    problemes.push('Indiquez si le bon est sous contrat ou facturé (jamais présélectionné : c’est à vous de le choisir).');
  }

  const releves = relevesEnvoyes(bon, contexte.compteurs);
  if (releves.length === 0 && bon.lignes.length === 0) {
    problemes.push(
      modele === 'DELIVERY'
        ? 'Le bon est vide : saisissez au moins un matériel livré.'
        : 'Le bon est vide : saisissez au moins un relevé de compteur ou un article.',
    );
  }

  // Ce que la feuille du modèle sait porter : le papier n'a de place que pour cela.
  bon.lignes.forEach((l, i) => {
    if (!genrePermis(modele, l.kind)) problemes.push(`Ligne ${i + 1} : ce type de ligne n’existe pas sur la feuille de cette nature de bon.`);
  });
  if (modele === 'DELIVERY') {
    if (releves.length > 0) problemes.push('Cette nature de bon ne relève aucun compteur : retirez les relevés.');
    if (bon.levelKey !== null) problemes.push('Cette nature de bon ne réalise aucun palier d’entretien : retirez le palier réalisé.');
  }
  if ((modele === 'DELIVERY' || modele === 'RENTAL') && bon.suites.length > 0) {
    problemes.push('Cette nature de bon ne laisse aucune suite à donner : retirez-les.');
  }
  if (modele === 'REPAIR') {
    if (bon.details.problemeSignale.trim() === '') problemes.push('Le problème signalé n’est pas renseigné.');
    if (bon.details.travauxRealises.trim() === '') problemes.push('Les travaux réalisés ne sont pas renseignés.');
  }

  bon.lignes.forEach((l, i) => {
    if (l.designation.trim() === '') problemes.push(`Ligne ${i + 1} : la désignation n’est pas saisie.`);
    const lecture = lireDecimal(l.quantite, OPTIONS_QUANTITE);
    if (lecture.ok) {
      if (lecture.vide) problemes.push(`Ligne ${i + 1} : la quantité n’est pas saisie.`);
      else if (Number(lecture.valeur) <= 0) problemes.push(`Ligne ${i + 1} : la quantité doit être supérieure à zéro.`);
    }
  });

  bon.suites.forEach((s, i) => {
    if (s.comment.trim() === '') problemes.push(`Suite ${i + 1} : le commentaire est obligatoire (que faut-il prévoir ?).`);
  });

  if (bon.levelKey !== null) {
    if (!contexte.natureEnregistreEntretien) {
      problemes.push('Cette nature n’enregistre pas d’entretien : retirez le palier réalisé.');
    } else if (contexte.paliers.length === 0) {
      problemes.push('Aucune gamme n’est rattachée à cette machine : le palier réalisé ne peut pas être enregistré.');
    } else if (!contexte.paliers.some((p) => p.levelKey === bon.levelKey)) {
      problemes.push('Ce palier n’existe pas dans la version en vigueur de la gamme.');
    }
    const designe = contexte.compteurs.find((c) => c.drivesMaintenance);
    if (!designe) {
      problemes.push('Aucun compteur n’est désigné pour les entretiens de cette machine : désignez celui des heures totales.');
    } else if (!releves.some((r) => r.counterId === designe.id)) {
      problemes.push(`Le palier réalisé exige les heures du compteur « ${designe.label} » : relevez-le dans ce bon.`);
    }
  }

  return problemes;
}

// ------------------------------------------------------------------- vers l'API

/** Le contenu envoyé : ce qui est renseigné, normalisé. */
export function contenuEnvoye(bon: BonEditable, compteurs: readonly Compteur[]): ContenuBon {
  return {
    readings: relevesEnvoyes(bon, compteurs),
    lines: bon.lignes.map((l) => {
      const lecture = lireDecimal(l.quantite, OPTIONS_QUANTITE);
      return {
        lineKey: l.lineKey,
        kind: l.kind,
        reference: l.reference.trim() === '' ? null : l.reference.trim(),
        designation: l.designation.trim(),
        quantity: lecture.ok && !lecture.vide ? lecture.valeur : null,
        unit: l.unite.trim() === '' ? null : l.unite.trim(),
        note: l.note.trim() === '' ? null : l.note.trim(),
        serialNumber: l.kind === 'EQUIPMENT' && l.serie.trim() !== '' ? l.serie.trim() : null,
        ...(l.versee ? { versedFrom: { profileId: l.versee.gammeId, levelCode: l.versee.palier } } : {}),
      };
    }),
    levelKey: bon.levelKey,
    followUps: bon.suites.map((s) => ({ followUpKey: s.followUpKey, kind: s.kind, comment: s.comment.trim() })),
    technicians: bon.techniciens,
    checks: bon.controles.map((c): ControleBon => ({ checkKey: c.checkKey, label: c.label, checked: c.checked })),
    details: {
      reportedProblem: bon.details.problemeSignale.trim(),
      workDone: bon.details.travauxRealises.trim(),
      reservations: bon.details.reserves.trim(),
      dossier: bon.details.dossier === '' ? null : bon.details.dossier,
      rentalFrom: bon.details.locationDu === '' ? null : bon.details.locationDu,
      rentalReturn: bon.details.locationAu === '' ? null : bon.details.locationAu,
    },
  };
}

const ouNull = (texte: string): string | null => (texte.trim() === '' ? null : texte.trim());

/** Sur quoi le bon est créé : une machine, ou (livraison seulement) un site. Une chaîne seule est l'identifiant d'une machine. */
export type CibleBon = string | { readonly assetId: string } | { readonly siteId: string };

/** La création : les champs vides ne sont pas envoyés (le bon naît avec ce qui est renseigné). */
export function entreeCreation(
  bon: BonEditable,
  cible: CibleBon,
  compteurs: readonly Compteur[],
  clientKey: string,
): EntreeCreationBon {
  const sur = typeof cible === 'string' ? { assetId: cible } : cible;
  return {
    natureId: bon.natureId,
    ...sur,
    clientKey,
    ...(bon.facturation === '' ? {} : { billing: bon.facturation }),
    ...(ouNull(bon.praxedoNumber) === null ? {} : { praxedoNumber: bon.praxedoNumber.trim() }),
    ...(ouNull(bon.contactLabel) === null ? {} : { contactLabel: bon.contactLabel.trim() }),
    ...(bon.performedOn === '' ? {} : { performedOn: bon.performedOn }),
    ...(ouNull(bon.observations) === null ? {} : { observations: bon.observations.trim() }),
    content: contenuEnvoye(bon, compteurs),
  };
}

/** La sauvegarde d'un brouillon existant : tout est envoyé, un champ vidé part en `null` (absent = inchangé côté serveur). */
export function entreeSauvegarde(bon: BonEditable, revision: number, compteurs: readonly Compteur[]): EntreeSauvegardeBon {
  return {
    revision,
    natureId: bon.natureId,
    praxedoNumber: ouNull(bon.praxedoNumber),
    contactLabel: ouNull(bon.contactLabel),
    performedOn: bon.performedOn === '' ? null : bon.performedOn,
    observations: ouNull(bon.observations),
    billing: bon.facturation === '' ? null : bon.facturation,
    content: contenuEnvoye(bon, compteurs),
    // Les heures de l'équipe : envoyées seulement s'il y en a (absent = rien n'est touché dans l'équipe).
    ...(heuresEnvoyees(bon.heures).length > 0 ? { heures: heuresEnvoyees(bon.heures) } : {}),
  };
}
